/**
 * Documentation ▸ **Release documents** (`/documentation/releases`,
 * 2026-09-17) — every release, newest first, as folders.
 *
 * The CEO: the documents for each release are placed the way they always
 * were (on the version, in the command center) but must ALSO appear in
 * Documentation, in a folder of their own, where you open a release and see
 * all its documents. This is that folder. It reuses the Releases page's
 * folder tile and the same `useReleases` read (one query key, so moving
 * between the two sections paints from cache); only the destination differs.
 */
import { Box, Button, Card, CardContent, Typography } from "@mui/material";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";

import { BackButton } from "../../components/ui/back-button.js";
import { PageHeader } from "../../components/ui/page-header.js";
import { PageLoader } from "../../components/ui/page-loader.js";
import { DownloadRefused } from "../portal/releases/api.js";
import { ReleaseFolderCard } from "../portal/releases/ReleaseFolderCard.js";
import { useReleases } from "../portal/releases/useReleases.js";
import { KB_PATH, kbReleasePath } from "./kb-paths.js";

export const RELEASE_DOCUMENTS_TITLE = "Release documents";

export function ReleaseDocumentsIndexPage(): JSX.Element {
  const navigate = useNavigate();
  const { families, isLoading, isError, error, refetch } = useReleases();

  return (
    <Box sx={{ display: "grid", gap: 2 }} data-testid="kb-release-documents">
      <BackButton to={KB_PATH} label="Documentation" size="small" sx={{ justifySelf: "start" }} />
      <PageHeader
        title={RELEASE_DOCUMENTS_TITLE}
        description="The installation and management guides that ship with each release. Open a release to see its versions."
      />
      <Card>
        <CardContent sx={{ px: 2.5, py: 2 }}>
          {isLoading ? (
            <PageLoader />
          ) : isError ? (
            <Box sx={{ borderRadius: 1, border: 1, borderColor: "error.main", p: 2, fontSize: 14 }}>
              <Typography sx={{ mb: 1, color: "error.main" }}>
                Could not load releases
                {error instanceof DownloadRefused && error.message ? `: ${error.message}` : "."}
              </Typography>
              <Button variant="contained" size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            </Box>
          ) : families.length === 0 ? (
            <Typography
              data-testid="kb-release-documents-empty"
              sx={{ py: 3, textAlign: "center", fontSize: 14, color: "text.disabled" }}
            >
              No released versions yet.
            </Typography>
          ) : (
            <Box
              data-testid="kb-release-folders"
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
              }}
            >
              {families.map((family) => (
                <ReleaseFolderCard
                  key={family.line.id}
                  family={family}
                  onOpen={() => navigate(kbReleasePath(family.line.version))}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ReleaseDocumentsIndexPage;
